import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

const deliveryOptions = [
  { value: "intern", label: "Nur intern" },
  { value: "email", label: "Nur E-Mail" },
  { value: "both", label: "Intern & E-Mail" },
];

export default function BroadcastForm({ onCancel, onSend }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [format, setFormat] = useState('text'); // text | markdown | html
  const [bodyHtml, setBodyHtml] = useState('');
  const quillContainerRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const [recipientType, setRecipientType] = useState("all");
  const [recipientValue, setRecipientValue] = useState("");
  const [delivery, setDelivery] = useState("intern");
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    api.get("/roles").then(res => setRoles(res.data));
    api.get("/users/list").then(res => setUsers(res.data.items));
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    let recipient = { type: recipientType };
    if (recipientType === "role") recipient.value = recipientValue;
    if (recipientType === "user") recipient.value = recipientValue;
    const submitDelivery = recipientType === 'newsletter' ? 'email' : delivery;
    const payload = { subject, body, recipient, delivery: submitDelivery };
    if (format === 'html') {
      const safe = DOMPurify.sanitize(bodyHtml || '', { USE_PROFILES: { html: true } });
      payload.bodyHtml = safe;
    }
    if (format === 'markdown') {
      const raw = marked.parse(body || '');
      const safe = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
      payload.bodyHtml = safe;
    }
    onSend(payload);
  }

  function markdownPreview(src) {
    const raw = marked.parse(src || '');
    return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
  }

  useEffect(() => {
    if (recipientType === 'newsletter') {
      // Nur laden, wenn noch keine Vorschau oder manuell Refresh
      if (!newsletterPreview) fetchNewsletterPreview();
    }
  }, [recipientType]);

  function fetchNewsletterPreview(force = false) {
    if (loadingPreview) return;
    if (!force && newsletterPreview) return; // bereits geladen
    setLoadingPreview(true);
    setPreviewError("");
    api.get('/rundmail/newsletter/preview?limit=10')
      .then(res => setNewsletterPreview(res.data))
      .catch(() => setPreviewError('Fehler beim Laden der Newsletter-Vorschau.'))
      .finally(() => setLoadingPreview(false));
  }

  // Initialize / destroy Quill instance when switching to HTML format
  useEffect(() => {
    if (format !== 'html') {
      // Tear down
      if (quillInstanceRef.current) {
        quillInstanceRef.current = null;
      }
      return;
    }
    if (quillInstanceRef.current || !quillContainerRef.current) return;
    const toolbar = document.createElement('div');
    toolbar.className = 'quill-custom-toolbar';
    quillContainerRef.current.innerHTML = '';
    quillContainerRef.current.appendChild(toolbar);
    const editorDiv = document.createElement('div');
    quillContainerRef.current.appendChild(editorDiv);
    const q = new Quill(editorDiv, {
      theme: 'snow',
      modules: { toolbar: quillToolbarConfig }
    });
    quillInstanceRef.current = q;
    if (bodyHtml) {
      q.root.innerHTML = bodyHtml;
    }
    q.on('text-change', () => {
      setBodyHtml(q.root.innerHTML);
    });
  }, [format]);

  return (
    <form onSubmit={handleSubmit} style={{ background: "#222", borderRadius: 12, padding: 24, marginBottom: 24 }}>
      <h3 style={{ marginTop: 0 }}>Neue Rundmail erstellen</h3>
      <div style={{ marginBottom: 16 }}>
        <label>Betreff<br />
          <input value={subject} onChange={e => setSubject(e.target.value)} required style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff" }} />
        </label>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
          <label style={{ color:'#ccc' }}>Formatierung:
            <select value={format} onChange={e => setFormat(e.target.value)} style={{ marginLeft:6, background:'#181818', color:'#fff', border:'1px solid #444', borderRadius:6, padding:'4px 8px' }}>
              <option value="text">Einfach (Text)</option>
              <option value="markdown">Markdown</option>
              <option value="html">HTML</option>
            </select>
          </label>
        </div>
        {format === 'text' && (
          <label>Nachricht<br />
            <textarea value={body} onChange={e => setBody(e.target.value)} required rows={8} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff" }} />
          </label>
        )}
        {format === 'markdown' && (
          <label>Markdown<br />
            <textarea value={body} onChange={e => setBody(e.target.value)} required rows={10} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff", fontFamily:'monospace' }} />
          </label>
        )}
        {format === 'html' && (
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
              <button type="button" onClick={() => insertCta(quillInstanceRef.current)} style={quickBtnStyle}>CTA-Button</button>
              <button type="button" onClick={() => insertDivider(quillInstanceRef.current)} style={quickBtnStyle}>Divider</button>
              <button type="button" onClick={() => insertInfoBox(quillInstanceRef.current)} style={quickBtnStyle}>Info-Box</button>
              <button type="button" onClick={() => triggerImageUpload(quillInstanceRef.current)} style={quickBtnStyle}>Bild hochladen</button>
            </div>
            <div ref={quillContainerRef} style={{ background:'#fff', color:'#000', borderRadius:6 }} />
          </div>
        )}
        <div style={{ marginTop:12, background:'#1b1b1b', border:'1px solid #333', borderRadius:8 }}>
          <div style={{ padding:'8px 12px', borderBottom:'1px solid #2a2a2a', color:'#bbb', fontSize:13 }}>Vorschau</div>
          <div style={{ padding:12 }}>
            {format === 'text' && (
              <div style={{ whiteSpace:'pre-wrap', color:'#ddd', lineHeight:1.5 }}>{body}</div>
            )}
            {format === 'markdown' && (
              <div style={{ background:'#fff', color:'#111', borderRadius:6, padding:12 }} dangerouslySetInnerHTML={{ __html: markdownPreview(body) }} />
            )}
            {format === 'html' && (
              <iframe title="html-live-preview" style={{ width:'100%', height:'40vh', background:'#fff', border:'1px solid #333', borderRadius:6 }} srcDoc={DOMPurify.sanitize(bodyHtml || '')}></iframe>
            )}
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label>Empfänger<br />
          <select value={recipientType} onChange={e => { setRecipientType(e.target.value); setRecipientValue(""); }} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff", marginBottom: 8 }}>
            <option value="all">Alle Benutzer</option>
            <option value="role">Rolle</option>
            <option value="user">Einzelner Nutzer</option>
            <option value="newsletter">Newsletter-Abonnenten</option>
          </select>
          {recipientType === "role" && (
            <select value={recipientValue} onChange={e => setRecipientValue(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff", marginTop: 8 }}>
              <option value="">Rolle wählen...</option>
              {roles.map(r => (
                <option key={r.name} value={r.name}>{r.label}</option>
              ))}
            </select>
          )}
          {recipientType === "user" && (
            <select value={recipientValue} onChange={e => setRecipientValue(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: "#181818", color: "#fff", marginTop: 8 }}>
              <option value="">Nutzer wählen...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.displayName || u.username}</option>
              ))}
            </select>
          )}
          {recipientType === "newsletter" && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#aaa' }}>
              Versand erfolgt ausschließlich per E-Mail an bestätigte Newsletter-Abonnenten.
              <div style={{ marginTop: 8, background: '#181818', padding: 10, borderRadius: 6, border: '1px solid #333' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <strong style={{ fontSize: 12 }}>Newsletter Vorschau</strong>
                  <button type="button" onClick={() => { setNewsletterPreview(null); fetchNewsletterPreview(true); }} style={{ background:'#333', color:'#fff', border:'none', borderRadius:4, padding:'4px 8px', cursor:'pointer', fontSize:11 }}>Neu laden</button>
                </div>
                {loadingPreview && <div style={{ color:'#888', marginTop:6 }}>Lade Vorschau...</div>}
                {previewError && <div style={{ color:'#e53935', marginTop:6 }}>{previewError}</div>}
                {newsletterPreview && !loadingPreview && !previewError && (
                  <div style={{ marginTop:6 }}>
                    <div style={{ marginBottom:6 }}>Bestätigte Abonnenten: <strong>{newsletterPreview.count}</strong></div>
                    {newsletterPreview.preview && newsletterPreview.preview.length > 0 ? (
                      <ul style={{ listStyle:'none', padding:0, margin:0, maxHeight:120, overflowY:'auto', fontSize:11 }}>
                        {newsletterPreview.preview.map(em => (
                          <li key={em} style={{ padding:'2px 0', borderBottom:'1px solid #262626' }}>{em}</li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize:11, color:'#777' }}>Keine E-Mails in Vorschau.</div>
                    )}
                    {newsletterPreview.count > (newsletterPreview.preview?.length || 0) && (
                      <div style={{ marginTop:6, fontSize:11, color:'#999' }}>… weitere {(newsletterPreview.count - (newsletterPreview.preview?.length || 0))} nicht angezeigt.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </label>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label>Versandoption<br />
          <select value={recipientType === 'newsletter' ? 'email' : delivery} disabled={recipientType === 'newsletter'} onChange={e => setDelivery(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #444", background: recipientType==='newsletter' ? '#202020' : '#181818', color: "#fff", opacity: recipientType==='newsletter' ? 0.7 : 1 }}>
            {deliveryOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {recipientType === 'newsletter' && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#ffca28' }}>Für Newsletter-Abonnenten ist nur E-Mail Versand möglich.</div>
          )}
        </label>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button type="submit" style={{ padding: "8px 20px", borderRadius: 8, background: "#1976d2", color: "#fff", border: "none", fontWeight: 500, cursor: "pointer" }}>Senden</button>
        <button type="button" onClick={onCancel} style={{ padding: "8px 20px", borderRadius: 8, background: "#444", color: "#fff", border: "none", fontWeight: 500, cursor: "pointer" }}>Abbrechen</button>
      </div>
    </form>
  );
}

// Styles & helper definitions (outside component scope if needed)
const quickBtnStyle = {
  background:'#333', color:'#fff', border:'none', padding:'6px 10px', borderRadius:6, cursor:'pointer', fontSize:12
};

const quillToolbarConfig = [
  [{ header: [1,2,3,false] }],
  ['bold','italic','underline','strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean']
];

function insertHtmlSnippet(quill, html) {
  if (!quill) return;
  const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
  // Use Quill clipboard to insert HTML at the current caret
  quill.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
  quill.setSelection(Math.min(range.index + 1, quill.getLength()), 0);
}

function insertCta(quill) {
  insertHtmlSnippet(quill, '<p style="text-align:center;"><a href="https://" style="display:inline-block;background:#1976d2;color:#fff;padding:12px 20px;border-radius:6px;text-decoration:none;font-weight:600;">Call To Action</a></p>');
}
function insertDivider(quill) { insertHtmlSnippet(quill, '<div style="border-top:1px solid #ccc;margin:24px 0;"></div>'); }
function insertInfoBox(quill) { insertHtmlSnippet(quill, '<div style="border:1px solid #1976d2;padding:12px;border-radius:8px;background:#f0f7ff;color:#124b7e;margin:12px 0;">Wichtige Information hier einfügen.</div>'); }

function triggerImageUpload(quill) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async () => {
    const file = input.files && input.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/rundmail/upload', fd, { headers: { 'Content-Type':'multipart/form-data' } });
      const url = data.url;
      if (url) {
        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        quill.insertEmbed(range.index, 'image', url, 'user');
        quill.setSelection(range.index + 1, 0);
      }
    } catch (e) {
      alert('Upload fehlgeschlagen: ' + (e?.response?.data?.error || e.message));
    }
  };
  input.click();
}

// Initialize Quill when format switches to html
// (Placing hook below component definition region for clarity)
// Using Effect inside component (append at end of file for patch simplicity):
// We'll monkey patch by reopening component scope via export default function side effect - keep minimal.

